import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Currency } from '@seapoint/server/enums';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Form, FormControl, FormField, FormItem, FormLabel } from '../../../../design-system/form/form-primitives';
import { Input } from '../../../../design-system/input/input';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView, BeneficiaryView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { getBankAccountName } from '../../../../utils/get-bank-account-name';
import { getCurrencySymbol } from '../../../../utils/get-currency-symbol';
import { AccountSelectItem } from '../../components/account-select-item';
import { PaymentInputField } from '../../components/payment-input-field';
import { CurrencyAccountNotFound } from '../../external-payment-flow/components/currency-account-not-found';
import { EvidencePrefill } from '../../payment-flow-storage';
import { canInitiateExternalPayments } from '../../utils/capability-checks';
import { BeneficiarySelectField } from './beneficiary-select-field';
import { createInvoicePaymentInformationFormSchema } from './invoice-payment-information-form-validation-schema';

type InvoicePaymentInformationFormValues = z.infer<ReturnType<typeof createInvoicePaymentInformationFormSchema>>;

type InvoiceInformation = {
  totalAmount?: number;
  suggestedBeneficiaryId?: string | null;
  invoiceReference?: string;
};

type InvoicePaymentInformationFormProps = {
  accounts: AccountView[];
  initialSourceAccountId?: string;
  beneficiaries: BeneficiaryView[];
  invoiceInformation: InvoiceInformation;
  isLoading: boolean;
  currency: Currency;
  evidencePrefill: EvidencePrefill | null;
  onCancelTransfer: () => void;
  onSubmit: (values: InvoicePaymentInformationFormValues) => void;
  onPaymentDataChange: (values: InvoicePaymentInformationFormValues & { currency: Currency }) => void;
  setIsPaymentFormValid?: (isValid: boolean) => void;
};

export const InvoicePaymentInformationForm = ({
  accounts,
  initialSourceAccountId,
  beneficiaries,
  invoiceInformation,
  isLoading,
  currency,
  evidencePrefill,
  onCancelTransfer,
  onSubmit,
  onPaymentDataChange,
  setIsPaymentFormValid,
}: InvoicePaymentInformationFormProps) => {
  const availableSourceAccounts = accounts.filter((a) => a.currency === currency).filter(canInitiateExternalPayments);
  const defaultSourceAccount = initialSourceAccountId ?? availableSourceAccounts[0]?.id;
  const [selectedSourceAccountId, setSelectedSourceAccountId] = useState(defaultSourceAccount);
  const selectedSourceAccount = availableSourceAccounts.find((a) => a.id === selectedSourceAccountId);
  const hasSourceAccounts = availableSourceAccounts.length > 0;
  const availableBeneficiaries = beneficiaries.filter((b) => b.destination.currency === currency);

  const form = useForm({
    mode: 'onTouched',
    defaultValues: {
      amount: invoiceInformation.totalAmount ?? 0,
      sourceAccountId: selectedSourceAccountId,
      beneficiaryId: invoiceInformation.suggestedBeneficiaryId ?? '',
      paymentReference: invoiceInformation.invoiceReference ?? '',
    },
    resolver: zodResolver(
      createInvoicePaymentInformationFormSchema(currency, selectedSourceAccount?.availableBalance ?? 0),
    ),
    disabled: isLoading,
  });

  const { errors, isValid } = form.formState;

  const { subscribe } = form;

  useEffect(() => {
    const subscription = subscribe({
      callback: ({ values, isValid: isFormValid, isValidating }) => {
        if (isFormValid && !isValidating) {
          onPaymentDataChange({
            ...values,
            currency,
            // The amount comes back as a string from the form, but we need to store it as a number so the payment call doesn't fail
            amount: Number(values.amount),
          });
        }
      },
      formState: {
        values: true,
        isValid: true,
      },
    });

    return () => subscription();
  }, [onPaymentDataChange, subscribe]);

  useEffect(() => {
    setIsPaymentFormValid?.(isValid);
  }, [isValid, setIsPaymentFormValid]);

  useEffect(() => {
    // Triggers validation on amount field when source account is changed
    // Not running in on undefined prevents running it on initial render
    if (form.getValues('amount') !== undefined && selectedSourceAccountId) {
      form.trigger('amount');
    }

    form.trigger('paymentReference');
  }, [form, selectedSourceAccountId]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => onSubmit(data))} className="w-full">
        <Stack align="stretch" space={3}>
          <Stack align="stretch" space={1}>
            <Stack
              className={cn(
                'rounded-lg border border-border focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:outline-hidden',
                {
                  'border-destructive': !!errors.amount || !!errors.sourceAccountId,
                },
              )}
            >
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => {
                  const currencySymbol = getCurrencySymbol(currency);

                  return (
                    <FormItem>
                      <VisuallyHidden>
                        <FormLabel>Amount to be transferred</FormLabel>
                      </VisuallyHidden>

                      <FormControl>
                        <Input
                          required
                          leftIcon={<p className="text-base font-medium text-muted-foreground">{currency}</p>}
                          type="text"
                          placeholder={`${currencySymbol}0.00`}
                          rightAlign
                          className={cn(
                            '[appearance:textfield] rounded-t-lg rounded-b-none border-0 bg-white py-2 pr-4 pl-9 text-2xl placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
                          )}
                          {...field}
                          value={field.value ? `${currencySymbol}${field.value}` : ''}
                          onChange={(e) => {
                            // Remove currency symbol and non-numeric chars, then convert to number
                            const numericValue = e.target.value.replace(currencySymbol, '').replace(/[^\d.]/g, '');

                            field.onChange(numericValue);
                          }}
                          onFocus={(e) => {
                            if (e.target.value === currencySymbol + '0') {
                              e.target.value = currencySymbol;
                            }
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />

              {hasSourceAccounts && (
                <FormField
                  control={form.control}
                  name="sourceAccountId"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <VisuallyHidden>
                          <FormLabel htmlFor="from-account-select">From Account</FormLabel>
                        </VisuallyHidden>

                        <FormControl>
                          <Select
                            id="from-account-select"
                            aria-labelledby="from-account-label"
                            required
                            placeholder={
                              <AccountSelectItem
                                balance={availableSourceAccounts[0].availableBalance ?? 0}
                                currency={availableSourceAccounts[0].currency}
                                institutionLogo={availableSourceAccounts[0].institution?.logo}
                                name={availableSourceAccounts[0].displayName ?? 'Bank Account'}
                                fspAdapter={availableSourceAccounts[0].fspAdapter}
                              />
                            }
                            onValueChange={(value) => {
                              const selectedAccount = availableSourceAccounts.find((a) => a.id === value);

                              field.onChange(value);

                              if (selectedAccount) {
                                setSelectedSourceAccountId(selectedAccount.id);
                              }
                            }}
                            defaultValue={selectedSourceAccountId}
                            triggerClassName={cn(
                              'justify-start gap-2 rounded-t-none rounded-b-lg bg-muted py-1.5 focus:ring-0 focus:ring-offset-0 [&_.account-logo]:h-4 [&_.account-logo]:w-4 [&_.account-type]:hidden',
                            )}
                          >
                            {availableSourceAccounts.map((account) => {
                              const accountName = getBankAccountName({
                                displayName: account.displayName,
                                institutionName: account.institution?.name,
                              });

                              return (
                                <Select.Item key={account.id} value={account.id} className="py-3">
                                  <AccountSelectItem
                                    balance={account.availableBalance ?? 0}
                                    currency={account.currency}
                                    institutionLogo={account.institution?.logo}
                                    name={accountName}
                                    fspAdapter={account.fspAdapter}
                                  />
                                </Select.Item>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </FormItem>
                    );
                  }}
                />
              )}

              {!hasSourceAccounts && (
                // TODO: move component to a shared location
                <CurrencyAccountNotFound
                  currency={currency}
                  // TODO: not sure what is the flow for this
                  onOpenAccount={() => {}}
                  onCancelTransfer={onCancelTransfer}
                />
              )}
            </Stack>

            <Stack align="end" space={1}>
              {errors.amount && <p className="text-sm font-medium text-destructive">{errors.amount.message}</p>}

              {errors.sourceAccountId && (
                <p className="text-sm font-medium text-destructive">{errors.sourceAccountId.message}</p>
              )}
            </Stack>
          </Stack>

          <BeneficiarySelectField
            beneficiaries={availableBeneficiaries}
            badge={
              form.getValues('beneficiaryId') === evidencePrefill?.beneficiaryId ? 'Autofilled from invoice' : undefined
            }
          />

          <PaymentInputField
            form={form}
            label="Reference"
            inputName="paymentReference"
            placeholder="Invoice reference"
            badge={
              form.getValues('paymentReference') === evidencePrefill?.reference ? 'Autofilled from invoice' : undefined
            }
          />

          <Button
            variant="default"
            size="lg"
            type="submit"
            disabled={!isValid || !hasSourceAccounts}
            isLoading={isLoading}
          >
            Continue
          </Button>
        </Stack>
      </form>
    </Form>
  );
};
